import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IonButton, IonIcon, IonSpinner, IonToast } from '@ionic/react';
import { addOutline, trashOutline, repeatOutline, cloudDownloadOutline, chevronForwardOutline, chevronDownOutline } from 'ionicons/icons';
import {
  Expense, ExpenseSummary, listExpenses, expenseSummary,
  createExpense, updateExpense, deleteExpense, syncAnthropicExpenses,
} from '../services/expenseService';

// Costs are typed in a hurry, usually while looking at a card statement, so the whole screen is
// built around one row of inputs at the top: type an amount, hit Enter, it is saved. Everything
// else (category, vendor, recurrence) has a default and can be fixed inline afterwards.
const CURRENCIES = ['GBP', 'USD', 'EUR', 'AED'];
// Seeds for the datalists so the first few entries are still one keystroke; anything already used
// is merged in from the server.
const SEED_CATEGORIES = ['Software', 'Ticket tools', 'Domains', 'Team', 'Fees', 'Marketing', 'Hardware', 'Other'];
const SEED_VENDORS = ['Anthropic', 'TicketVault', 'OpenAI', 'Meta', 'AWS', 'Render', 'Firebase', 'GoDaddy'];

const todayISO = () => new Date().toISOString().slice(0, 10);
const money = (n: number) =>
  (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const CUR_SYMBOL: Record<string, string> = { GBP: '£', USD: '$', EUR: '€', AED: 'AED ' };

const emptyDraft = () => ({
  description: '', category: '', vendor: '', amount: '',
  currency: 'GBP', date: todayISO(), recurrence: 'once' as const, notes: '',
});

const ExpensesContent: React.FC = () => {
  const [rows, setRows] = useState<Expense[]>([]);
  const [sum, setSum] = useState<ExpenseSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [anthropicOpen, setAnthropicOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState<string>('');
  const descRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const [list, s] = await Promise.all([listExpenses(), expenseSummary()]);
      setRows(list);
      setSum(s);
    } catch {
      setToast('Could not load expenses');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  // Anthropic bills per day of API usage; the server files each day as its own USD row.
  const syncAnthropic = async () => {
    setSyncing(true);
    try {
      const r = await syncAnthropicExpenses();
      setToast(`Anthropic: ${r.days} day(s) since ${r.from}, ${r.created} added, ${r.updated} updated ($${r.totalUsd.toFixed(2)})`);
      await load();
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Anthropic sync failed — is ANTHROPIC_ADMIN_KEY set on the server?');
    } finally {
      setSyncing(false);
    }
  };

  const categories = useMemo(
    () => [...new Set([...(sum?.knownCategories || []), ...SEED_CATEGORIES])].sort(),
    [sum],
  );
  const vendors = useMemo(
    () => [...new Set([...(sum?.knownVendors || []), ...SEED_VENDORS])].sort(),
    [sum],
  );

  const add = async () => {
    const amount = Number(draft.amount);
    if (!Number.isFinite(amount) || amount === 0) { setToast('Enter an amount'); return; }
    setSaving(true);
    try {
      await createExpense({ ...draft, amount } as any);
      // Keep category/vendor/currency/date — entering several costs from one statement is the
      // normal case, and re-typing the same category every time is the thing that makes people
      // stop logging expenses at all.
      setDraft((d) => ({ ...emptyDraft(), category: d.category, vendor: d.vendor, currency: d.currency, date: d.date }));
      descRef.current?.focus();
      await load();
    } catch {
      setToast('Could not save');
    } finally {
      setSaving(false);
    }
  };

  const patch = async (id: string, field: string, value: any) => {
    setRows((prev) => prev.map((r) => (r._id === id ? { ...r, [field]: value } as Expense : r)));
    try {
      const updated = await updateExpense(id, { [field]: value } as any);
      setRows((prev) => prev.map((r) => (r._id === id ? updated : r)));
      void expenseSummary().then(setSum).catch(() => undefined);
    } catch {
      setToast('Could not update');
      void load();
    }
  };

  const remove = async (id: string) => {
    setRows((prev) => prev.filter((r) => r._id !== id));
    try { await deleteExpense(id); void expenseSummary().then(setSum).catch(() => undefined); }
    catch { setToast('Could not delete'); void load(); }
  };

  const visible = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (catFilter && (r.category || 'Uncategorised') !== catFilter) return false;
      if (!term) return true;
      return [r.description, r.category, r.vendor, r.notes].some((v) => String(v || '').toLowerCase().includes(term));
    });
  }, [rows, q, catFilter]);

  // The Anthropic sync writes a row per day, which would bury everything else. Fold them into one
  // line (placed where the newest day sits) that opens to show the days. Nothing here changes what
  // is stored or the totals — it's purely how the list reads.
  type Item = { kind: 'row'; r: Expense } | { kind: 'anthropic'; days: Expense[]; usd: number; gbp: number; first: string; last: string };
  const items = useMemo<Item[]>(() => {
    const days = visible.filter((r) => r.source === 'anthropic');
    if (days.length < 2) return visible.map((r) => ({ kind: 'row', r }));
    const out: Item[] = [];
    let placed = false;
    for (const r of visible) {
      if (r.source !== 'anthropic') { out.push({ kind: 'row', r }); continue; }
      if (placed) continue;
      placed = true;
      const dates = days.map((d) => String(d.date).slice(0, 10)).sort();
      out.push({
        kind: 'anthropic', days,
        usd: days.reduce((a, d) => a + (Number(d.amount) || 0), 0),
        gbp: days.reduce((a, d) => a + (Number(d.amountGBP) || 0), 0),
        first: dates[0], last: dates[dates.length - 1],
      });
    }
    return out;
  }, [visible]);
  const fmtDay = (iso: string) => new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const visibleTotal = useMemo(() => visible.reduce((n, r) => n + (Number(r.amountGBP) || 0), 0), [visible]);

  const cell = 'px-2 py-1 rounded border border-transparent hover:border-slate-300 dark:hover:border-slate-600 bg-transparent focus:border-blue-500 focus:outline-none text-sm w-full';

  if (loading) return <div className="flex justify-center py-16"><IonSpinner /></div>;

  const renderRow = (r: Expense) => (
      <tr key={r._id} className="border-t border-slate-100 dark:border-slate-800">
        <td className="px-1 py-0.5 w-36">
          <input type="date" className={cell} value={String(r.date || '').slice(0, 10)}
            onChange={(e) => patch(r._id, 'date', e.target.value)} />
        </td>
        <td className="px-1 py-0.5 min-w-[200px]">
          <input className={cell} defaultValue={r.description}
            onBlur={(e) => e.target.value !== r.description && patch(r._id, 'description', e.target.value)} />
        </td>
        <td className="px-1 py-0.5 w-36">
          <input list="expense-categories" className={cell} defaultValue={r.category}
            onBlur={(e) => e.target.value !== r.category && patch(r._id, 'category', e.target.value)} />
        </td>
        <td className="px-1 py-0.5 w-36">
          <input list="expense-vendors" className={cell} defaultValue={r.vendor}
            onBlur={(e) => e.target.value !== r.vendor && patch(r._id, 'vendor', e.target.value)} />
        </td>
        <td className="px-1 py-0.5 w-32 whitespace-nowrap">
          <span className="text-slate-400 text-xs">{CUR_SYMBOL[r.currency] || r.currency}</span>
          <input type="number" step="0.01" className={cell + ' inline-block w-24'} defaultValue={r.amount}
            onBlur={(e) => Number(e.target.value) !== r.amount && patch(r._id, 'amount', Number(e.target.value))} />
        </td>
        <td className="px-2 py-0.5 w-24 text-right tabular-nums text-gray-800 dark:text-gray-100">
          £{money(r.amountGBP)}
        </td>
        <td className="px-1 py-0.5 w-28">
          <select className={cell} value={r.recurrence} onChange={(e) => patch(r._id, 'recurrence', e.target.value)}>
            <option value="once">One-off</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </td>
        <td className="px-1 py-0.5 min-w-[140px]">
          <input className={cell} defaultValue={r.notes}
            onBlur={(e) => e.target.value !== r.notes && patch(r._id, 'notes', e.target.value)} />
        </td>
        <td className="px-1 py-0.5 w-10 text-center">
          <button onClick={() => remove(r._id)} title="Delete" className="text-red-500 hover:text-red-600">
            <IonIcon icon={trashOutline} />
          </button>
        </td>
      </tr>
  );

  return (
    <div className="p-3 space-y-3">
      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { label: 'This month', value: sum?.thisMonth ?? 0 },
          { label: 'This year', value: sum?.thisYear ?? 0 },
          { label: 'Monthly run-rate', value: sum?.monthlyRunRate ?? 0, hint: 'recurring costs, per month' },
          { label: 'All time', value: sum?.total ?? 0, hint: `${sum?.count ?? 0} entries` },
        ].map((c) => (
          <div key={c.label} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-900 px-3 py-2">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">{c.label}</div>
            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">£{money(c.value)}</div>
            {c.hint && <div className="text-[11px] text-slate-400">{c.hint}</div>}
          </div>
        ))}
      </div>

      {/* Add row — amount + Enter is the whole flow */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-900 p-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex-1 min-w-[180px]">
            <span className="text-[11px] uppercase tracking-wide text-slate-400">What for</span>
            <input
              ref={descRef}
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') void add(); }}
              placeholder="Anthropic API, ticketvault, domain…"
              className="mt-0.5 w-full px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm"
            />
          </label>
          <label className="w-32">
            <span className="text-[11px] uppercase tracking-wide text-slate-400">Amount</span>
            <input
              type="number" step="0.01" inputMode="decimal"
              value={draft.amount}
              onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') void add(); }}
              placeholder="0.00"
              className="mt-0.5 w-full px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm"
            />
          </label>
          <label className="w-24">
            <span className="text-[11px] uppercase tracking-wide text-slate-400">Currency</span>
            <select
              value={draft.currency}
              onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
              className="mt-0.5 w-full px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm"
            >
              {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label className="w-36">
            <span className="text-[11px] uppercase tracking-wide text-slate-400">Category</span>
            <input
              list="expense-categories"
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') void add(); }}
              placeholder="Software"
              className="mt-0.5 w-full px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm"
            />
          </label>
          <label className="w-36">
            <span className="text-[11px] uppercase tracking-wide text-slate-400">Paid to</span>
            <input
              list="expense-vendors"
              value={draft.vendor}
              onChange={(e) => setDraft({ ...draft, vendor: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') void add(); }}
              placeholder="Anthropic"
              className="mt-0.5 w-full px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm"
            />
          </label>
          <label className="w-36">
            <span className="text-[11px] uppercase tracking-wide text-slate-400">Date</span>
            <input
              type="date"
              value={draft.date}
              onChange={(e) => setDraft({ ...draft, date: e.target.value })}
              className="mt-0.5 w-full px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm"
            />
          </label>
          <label className="w-28">
            <span className="text-[11px] uppercase tracking-wide text-slate-400">Repeats</span>
            <select
              value={draft.recurrence}
              onChange={(e) => setDraft({ ...draft, recurrence: e.target.value as any })}
              className="mt-0.5 w-full px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm"
            >
              <option value="once">One-off</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </label>
          <IonButton size="small" onClick={add} disabled={saving}>
            <IonIcon slot="start" icon={addOutline} />
            Add
          </IonButton>
        </div>
        <div className="flex items-center justify-between gap-2 mt-1">
          <p className="text-[11px] text-slate-400">
            Enter saves it. Category, vendor, currency and date stay put so a run of costs from one statement is quick.
          </p>
          <IonButton size="small" fill="outline" onClick={syncAnthropic} disabled={syncing} title="Pull Anthropic API spend from their cost report (runs itself every morning)">
            {syncing ? <IonSpinner name="dots" className="w-4 h-4" /> : <IonIcon slot="start" icon={cloudDownloadOutline} />}
            Sync Anthropic
          </IonButton>
        </div>
      </div>

      <datalist id="expense-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
      <datalist id="expense-vendors">{vendors.map((v) => <option key={v} value={v} />)}</datalist>

      {/* Category chips + search */}
      <div className="flex flex-wrap items-center gap-1.5">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search…"
          className="w-44 px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-gray-900 text-sm"
        />
        {(sum?.byCategory || []).map((c) => (
          <button
            key={c.category}
            type="button"
            onClick={() => setCatFilter((prev) => (prev === c.category ? '' : c.category))}
            className={
              'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ' +
              (catFilter === c.category
                ? 'bg-blue-600 border-blue-600 text-white'
                : 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700')
            }
          >
            {c.category} · £{money(c.amount)}
          </button>
        ))}
        <span className="ml-auto text-sm text-slate-500">
          {visible.length} shown · <span className="font-semibold text-gray-800 dark:text-gray-100">£{money(visibleTotal)}</span>
        </span>
      </div>

      {/* Table — every cell edits in place */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-900">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 dark:bg-gray-800 text-slate-500">
            <tr>
              {['Date', 'What for', 'Category', 'Paid to', 'Amount', 'In GBP', 'Repeats', 'Notes', ''].map((h) => (
                <th key={h} className="text-left font-medium px-2 py-1.5 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((it) => it.kind === 'row' ? renderRow(it.r) : (
              <React.Fragment key="anthropic-group">
                <tr
                  className="border-t border-slate-100 dark:border-slate-800 bg-amber-50/60 dark:bg-amber-900/10 cursor-pointer select-none"
                  onClick={() => setAnthropicOpen((o) => !o)}
                  title={anthropicOpen ? 'Hide the daily charges' : 'Show the daily charges'}
                >
                  <td className="px-2 py-1 w-36 whitespace-nowrap text-slate-600 dark:text-slate-300">
                    <IonIcon icon={anthropicOpen ? chevronDownOutline : chevronForwardOutline} className="align-middle mr-1" />
                    {fmtDay(it.first)} → {fmtDay(it.last)}
                  </td>
                  <td className="px-2 py-1 min-w-[200px] font-medium text-gray-800 dark:text-gray-100">
                    Anthropic API usage <span className="text-slate-400 font-normal">· {it.days.length} days, synced daily</span>
                  </td>
                  <td className="px-2 py-1 w-36 text-slate-600 dark:text-slate-300">Software</td>
                  <td className="px-2 py-1 w-36 text-slate-600 dark:text-slate-300">Anthropic</td>
                  <td className="px-2 py-1 w-32 whitespace-nowrap tabular-nums text-gray-800 dark:text-gray-100">${money(it.usd)}</td>
                  <td className="px-2 py-1 w-24 text-right tabular-nums text-gray-800 dark:text-gray-100">£{money(it.gbp)}</td>
                  <td className="px-2 py-1 w-28 text-slate-400 text-xs">per day</td>
                  <td className="px-2 py-1 min-w-[140px] text-slate-400 text-xs">{anthropicOpen ? 'Click to collapse' : 'Click to see each day'}</td>
                  <td className="px-1 py-0.5 w-10" />
                </tr>
                {anthropicOpen && it.days.map((d) => (
                  <tr key={d._id} className="border-t border-slate-100 dark:border-slate-800 bg-amber-50/30 dark:bg-amber-900/5 text-xs">
                    <td className="px-2 py-0.5 pl-8 whitespace-nowrap text-slate-500">{fmtDay(String(d.date).slice(0, 10))}</td>
                    <td className="px-2 py-0.5 text-slate-500" colSpan={3}>{d.description}</td>
                    <td className="px-2 py-0.5 whitespace-nowrap tabular-nums text-slate-700 dark:text-slate-200">${money(d.amount)}</td>
                    <td className="px-2 py-0.5 text-right tabular-nums text-slate-700 dark:text-slate-200">£{money(d.amountGBP)}</td>
                    <td className="px-2 py-0.5" />
                    <td className="px-2 py-0.5 text-slate-500">{d.notes}</td>
                    <td className="px-1 py-0.5 text-center">
                      <button onClick={() => remove(d._id)} title="Delete this day" className="text-red-400 hover:text-red-600">
                        <IonIcon icon={trashOutline} />
                      </button>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {!visible.length && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400">
                {rows.length ? 'Nothing matches that filter.' : 'No expenses yet — add the first one above.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {!!sum?.monthlyRunRate && (
        <p className="text-xs text-slate-500 flex items-center gap-1">
          <IonIcon icon={repeatOutline} />
          Recurring costs commit £{money(sum.monthlyRunRate)}/month (£{money(sum.monthlyRunRate * 12)}/year).
        </p>
      )}

      <IonToast isOpen={!!toast} message={toast || ''} duration={3000} onDidDismiss={() => setToast(null)} color="danger" />
    </div>
  );
};

export default ExpensesContent;
